import * as React from 'react';

import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody';
import { useGetParkingCards } from 'src/features/cards/api/useParkingsCardsRetrieve';
import useCalculateStayTime from 'src/features/cards/hooks/useCalculateStayTime';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings';
import { useParking } from 'src/providers/ParkingProvider';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import ImageModal from 'src/registry/ui/ImageModal';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { generalMessages } from 'src/utils/messages';

import type { ReactElement } from 'react';
import type { PrepaidCard } from 'src/features/subscriptions/types';

type SubscriptionDetailsSidePanelProps = {
    subscription?: PrepaidCard;
    isMobile?: boolean;
};

type ReservationTitleData = {
    title: string;
    value: string | null | undefined | ReactElement;
    control: any;
};

const SubscriptionDetailsSidePanel = ({ subscription, isMobile = false }: SubscriptionDetailsSidePanelProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const { calculateStayTime } = useCalculateStayTime();
    const { data: parkingZone } = useListSubParkings({ parkingId });

    const { data: vehicleInParkingData } = useGetParkingCards({
        parkingId: parkingId,
        id: subscription?.card_number ?? '',
    });

    const [stayTime, setStayTime] = React.useState('');

    React.useEffect(() => {
        const stayString = calculateStayTime(vehicleInParkingData);
        setStayTime(stayString);

        const interval = setInterval(() => {
            const stayString = calculateStayTime(vehicleInParkingData);
            setStayTime(stayString);
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, [calculateStayTime, vehicleInParkingData]);

    React.useEffect(() => {
        const ibmPlexMono = new FontFace(
            'IBM Plex Mono',
            'url(https://fonts.gstatic.com/s/ibmplexmono/v19/-F63fjptAgt5VM-kVkqdyU8n1isq131nj-otFQ.woff2)',
        );
        void ibmPlexMono.load().then(() => {
            document.fonts.add(ibmPlexMono);
        });
    }, []);

    const parkingZoneNames = parkingZone?.results.map((zone) => zone.name)?.join(', ');

    const VehicleStatusBadge = React.useCallback(() => {
        const status = vehicleInParkingData?.in_parking;
        const color = status ? 'emerald' : 'red';
        const name = status
            ? formatMessage(reservationMessages.inParking)
            : formatMessage(reservationMessages.notInParking);
        return (
            <Badge theme={color}>
                <FontAwesomeIcon icon={status ? faCheckCircle : faXmarkCircle} />
                <div>{name}</div>
            </Badge>
        );
    }, [vehicleInParkingData]);

    const vehicleIsNotInParking = vehicleInParkingData?.in_parking;

    const sidePanelBodyAttributeMapping: ReservationTitleData[] = [
        {
            title: formatMessage(reservationMessages.vehicleStatus),
            value: <VehicleStatusBadge />,
            control: parkingId,
        },
        {
            title: formatMessage(reservationMessages.parkingZones),
            value: vehicleIsNotInParking ? parkingZoneNames : '-',
            control: parkingZoneNames,
        },
        {
            title: formatMessage(reservationMessages.enteredAt),
            value: vehicleIsNotInParking
                ? getFormattedDateTimeForDetailPanel(vehicleInParkingData?.enter_datetime) || '-'
                : '-',
            control: vehicleInParkingData,
        },
        {
            title: formatMessage(reservationMessages.exited),
            value: !vehicleIsNotInParking
                ? getFormattedDateTimeForDetailPanel(vehicleInParkingData?.exit_time.toString()) || '-'
                : '-',
            control: vehicleInParkingData,
        },
        {
            title: formatMessage(reservationMessages.totalStay),
            value: vehicleIsNotInParking ? stayTime : '-',
            control: vehicleInParkingData,
        },
    ];

    const imagePath = `/parkings/images/${parkingId}/${vehicleInParkingData?.photo_path}`;

    return (
        <>
            <DialogHeader>
                <div className="flex items-center gap-2">
                    <DialogTitle className="pr-0 font-mono">{vehicleInParkingData?.card_name}</DialogTitle>
                </div>
            </DialogHeader>
            <DialogSeparator />
            <DialogBody>
                <ImageModal
                    image={imagePath}
                    alt={vehicleInParkingData?.card_name}
                    title={vehicleInParkingData?.card_number}
                    disableClick={!vehicleInParkingData?.photo_path}
                />
                <div className="mt-4 grid gap-2">
                    {sidePanelBodyAttributeMapping.map((details) => {
                        return (
                            <SidePanelBodyAttribute key={details.title}>
                                <SidePanelBodyAttributeTitle>{details.title}</SidePanelBodyAttributeTitle>
                                <SidePanelBodyAttributeBody control={details.control}>
                                    {details.value ?? '-'}
                                </SidePanelBodyAttributeBody>
                            </SidePanelBodyAttribute>
                        );
                    })}
                </div>
            </DialogBody>
            {isMobile && (
                <>
                    <DialogSeparator />
                    <DialogFooter className="flex flex-col gap-2 sm:flex-col">
                        <DialogClose asChild>
                            <Button variant="outline" className="flex-1">
                                <FormattedMessage {...generalMessages.close} />
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </>
            )}
        </>
    );
};

export default SubscriptionDetailsSidePanel;
